import { useMemo } from "react";
import clsx from "clsx";
import SelectDropdown, { type SelectOption } from "@/components/ui/select-dropdown";
import { Button } from "@/components/ui/button/Button";
import { CC_PANEL } from "@/components/case-checking/caseCheckingChrome";

const FILTER_DROPDOWN_WIDTH = "w-full min-w-full";

export function InsightsFilterBar({
  adviserFilter,
  caseTypeFilter,
  adviserOptions,
  caseTypeOptions,
  formatCaseTypeLabel,
  onAdviserChange,
  onCaseTypeChange,
  onClear,
  hasActiveFilter,
  adviserTestId = "insights-filter-adviser",
  caseTypeTestId = "insights-filter-case-type",
  clearTestId = "insights-filter-clear",
}: {
  adviserFilter: string;
  caseTypeFilter: string;
  adviserOptions: string[];
  caseTypeOptions: string[];
  formatCaseTypeLabel: (slug: string) => string;
  onAdviserChange: (value: string) => void;
  onCaseTypeChange: (value: string) => void;
  onClear: () => void;
  hasActiveFilter: boolean;
  adviserTestId?: string;
  caseTypeTestId?: string;
  clearTestId?: string;
}) {
  const adviserSelectOptions: SelectOption<string>[] = useMemo(
    () => [
      { value: "", label: "All advisers" },
      ...adviserOptions.map((name) => ({ value: name, label: name })),
    ],
    [adviserOptions],
  );

  const caseTypeSelectOptions: SelectOption<string>[] = useMemo(
    () => [
      { value: "", label: "All case types" },
      ...caseTypeOptions.map((slug) => ({
        value: slug,
        label: formatCaseTypeLabel(slug),
      })),
    ],
    [caseTypeOptions, formatCaseTypeLabel],
  );

  return (
    <div className={clsx(CC_PANEL, "flex flex-wrap items-end gap-3 py-3")}>
      <div className="min-w-0 flex-1 basis-full sm:basis-[calc(50%-0.375rem)]">
        <label className="mb-1.5 block px-0 text-xs font-medium text-slate-400">Adviser</label>
        <SelectDropdown
          options={adviserSelectOptions}
          value={adviserFilter}
          onChange={onAdviserChange}
          className="w-full [&>div]:!mt-0"
          buttonClassName="!cursor-pointer ring-1 ring-slate-200 dark:ring-white/10"
          dropdownWidth={FILTER_DROPDOWN_WIDTH}
          data-testid={adviserTestId}
          renderListItem={(opt) => (
            <div className="py-2 pr-10 pl-3">
              <span className="block whitespace-nowrap font-normal">{opt.label}</span>
            </div>
          )}
        />
      </div>
      <div className="min-w-0 flex-1 basis-full sm:basis-[calc(50%-0.375rem)]">
        <label className="mb-1.5 block px-0 text-xs font-medium text-slate-400">Case type</label>
        <SelectDropdown
          options={caseTypeSelectOptions}
          value={caseTypeFilter}
          onChange={onCaseTypeChange}
          className="w-full [&>div]:!mt-0"
          buttonClassName="!cursor-pointer ring-1 ring-slate-200 dark:ring-white/10"
          dropdownWidth={FILTER_DROPDOWN_WIDTH}
          data-testid={caseTypeTestId}
          renderListItem={(opt) => (
            <div className="py-2 pr-10 pl-3">
              <span className="block whitespace-nowrap font-normal">{opt.label}</span>
            </div>
          )}
        />
      </div>
      {hasActiveFilter ? (
        <Button
          plain size="unstyled"
          type="button"
          onClick={onClear}
          className="shrink-0 px-1 pb-2.5 pt-0 text-xs text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
          data-testid={clearTestId}
        >
          Clear filters
        </Button>
      ) : null}
    </div>
  );
}
